import { useEffect, useSyncExternalStore } from "react";
import {
  deletePropertyPin,
  fetchPropertyPins,
  savePropertyPin,
} from "../helpers/propertyPins";

/**
 * The company's job tracker pins, shared by every component that shows them.
 *
 * One store for the whole app: the map, the details page and up to a hundred
 * result cards read the same list, so it is fetched once per login and every
 * change shows up everywhere at once. Pins are the company's, so a teammate's
 * change is picked up by a quiet refetch when the tab comes back into view.
 *
 * status: idle (logged out) | loading | ready | error (retried on next use)
 *         | unavailable (401/403: no login or no active subscription;
 *           404: the backend has no pins endpoint yet)
 */
const EMPTY = { token: null, status: "idle", pins: [], byPropertyId: new Map() };

/** A quiet refetch happens on focus / tab return, at most this often. */
export const PIN_REFRESH_INTERVAL_MS = 30 * 1000;

let state = EMPTY;
let inflight = null;
let loadedAt = 0;
// Bumped by every save and removal here, so a refetch that began before one
// can't put back what the server said earlier.
let version = 0;
const listeners = new Set();

function setState(next) {
  const pins = next.pins !== undefined ? next.pins : state.pins;
  state = {
    ...state,
    ...next,
    pins,
    byPropertyId:
      next.pins !== undefined
        ? new Map(pins.map((pin) => [pin.property_id, pin]))
        : state.byPropertyId,
  };
  listeners.forEach((listener) => listener());
}

function onBackInView() {
  if (typeof document !== "undefined" && document.visibilityState === "hidden") return;
  refreshPropertyPins();
}

function subscribe(listener) {
  listeners.add(listener);
  // Listen for the tab coming back only while something shows pins.
  if (listeners.size === 1 && typeof window !== "undefined") {
    window.addEventListener("focus", onBackInView);
    document.addEventListener("visibilitychange", onBackInView);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && typeof window !== "undefined") {
      window.removeEventListener("focus", onBackInView);
      document.removeEventListener("visibilitychange", onBackInView);
    }
  };
}

function getSnapshot() {
  return state;
}

function currentToken() {
  try {
    return localStorage.getItem("authToken");
  } catch {
    return null;
  }
}

/**
 * `quiet`: a background refetch of pins already on screen. Nothing shows as
 * loading, and a failure keeps what is there instead of hiding it.
 */
export function loadPropertyPins({ force = false, quiet = false } = {}) {
  const token = currentToken();

  if (!token) {
    if (state !== EMPTY) {
      state = EMPTY;
      listeners.forEach((listener) => listener());
    }
    return Promise.resolve();
  }

  const settled = state.status === "ready" || state.status === "unavailable";
  if (!force && state.token === token && settled) {
    return Promise.resolve();
  }
  if (inflight && inflight.token === token) {
    return inflight.promise;
  }

  // Another login's pins must never show while this one's load.
  if (!quiet) {
    setState({ token, status: "loading", pins: state.token === token ? state.pins : [] });
  }

  const startedAt = version;
  const promise = fetchPropertyPins()
    .then((data) => {
      if (currentToken() !== token) return;
      if (version !== startedAt) {
        // A save or removal landed meanwhile and is newer than this answer.
        if (!quiet) setState({ status: "ready" });
        return;
      }
      loadedAt = Date.now();
      setState({
        status: "ready",
        pins: Array.isArray(data?.pins) ? data.pins : [],
        // Who is looking and who owns the account, to say "You" and "(owner)".
        viewerId: data?.viewer_id ?? null,
        ownerId: data?.owner_id ?? null,
      });
    })
    .catch((error) => {
      if (currentToken() !== token || quiet) return;
      const code = error?.response?.status;
      // 404: a backend without the pins endpoint yet. Offer nothing rather
      // than controls whose every click would fail.
      const unavailable = code === 401 || code === 403 || code === 404;
      setState({ status: unavailable ? "unavailable" : "error", pins: [] });
    })
    .finally(() => {
      if (inflight?.promise === promise) inflight = null;
    });

  inflight = { token, promise };
  return promise;
}

/**
 * A teammate's new, moved or removed pin shows up without a reload: when the
 * tab is back in view, refetch quietly if the list is older than the interval.
 */
export function refreshPropertyPins() {
  const token = currentToken();
  if (!token) return loadPropertyPins();
  // Someone else logged in meanwhile, or the last load failed: load properly.
  if (state.token !== token || state.status === "error") return loadPropertyPins();
  if (state.status !== "ready" || Date.now() - loadedAt < PIN_REFRESH_INTERVAL_MS) {
    return Promise.resolve();
  }
  return loadPropertyPins({ force: true, quiet: true });
}

/** Set or move a pin. Rejects with the request error so the caller can say why. */
export async function setPropertyPinStatus(propertyId, status) {
  const pin = await savePropertyPin(propertyId, status);
  if (pin) {
    version += 1;
    setState({
      pins: [pin, ...state.pins.filter((p) => p.id !== pin.id && p.property_id !== pin.property_id)],
    });
  }
  return pin;
}

export async function removePropertyPin(pin) {
  await deletePropertyPin(pin.id);
  version += 1;
  setState({ pins: state.pins.filter((p) => p.id !== pin.id) });
}

/** For tests: forget everything between cases. */
export function resetPropertyPinsStore() {
  state = EMPTY;
  inflight = null;
  loadedAt = 0;
  version = 0;
}

export default function usePropertyPins() {
  const snapshot = useSyncExternalStore(subscribe, getSnapshot);

  useEffect(() => {
    loadPropertyPins();
  }, []);

  return {
    pins: snapshot.pins,
    byPropertyId: snapshot.byPropertyId,
    status: snapshot.status,
    viewerId: snapshot.viewerId ?? null,
    ownerId: snapshot.ownerId ?? null,
    setStatus: setPropertyPinStatus,
    remove: removePropertyPin,
  };
}
